import React, { useEffect, useState } from 'react';
import { User } from 'types';
import { Chip, Grid, Box, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getEmployeeNotes } from 'core/API/employeeNotes';
import dayjs from 'dayjs';
import styles from './ShowUserSetting.module.scss';
import { htmlToPlainText } from 'shared/utils/htmlToPlainText';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

interface ShowUserSettingProps {
	user: User;
}

// Note bodies are user-authored HTML: convert to text without DOM parsing (SEC-013).
const stripHtmlTags = (html: string | undefined | null): string =>
	htmlToPlainText(html, { preserveLineBreaks: true });

const ShowUserSetting: React.FC<ShowUserSettingProps> = ({ user }) => {
	const [employeeNotes, setEmployeeNotes] = useState<any[] | null>(null);
	const [notesLoading, setNotesLoading] = useState(false);
	const [notesError, setNotesError] = useState<string | null>(null);
	const [notesExpanded, setNotesExpanded] = useState(false);

	// read-only view: no editing state required

	// reuse fetch logic

	useEffect(() => {
		const fetchNotes = async () => {
			setNotesLoading(true);
			setNotesError(null);
			try {
				const workplaceId = Number(user?.hierarchy?.id) || getCurrentWorkplaceId();
				const res = await getEmployeeNotes({ workplaceId });
				const list = Array.isArray(res?.member) ? res.member : (res?.items || []);

				const isAuthoredByUser = (note: any) => {
					const author = note?.author || note?.author || null;
					if (!author) return false;
					if (author.id !== undefined && author.id !== null) return Number(author.id) === Number(user?.id);
					if (author['@id']) {
						const idFromAtId = parseInt(String(author['@id']).split('/').pop() || '0');
						if (!isNaN(idFromAtId)) return idFromAtId === Number(user?.id);
					}
					const af = (author.firstName || author.first_name || '').trim().toLowerCase();
					const al = (author.lastName || author.last_name || '').trim().toLowerCase();
					const uf = (user?.firstName || (user as any)?.first_name || '').trim().toLowerCase();
					const ul = (user?.lastName || (user as any)?.last_name || '').trim().toLowerCase();
					if (af && al && uf && ul) return af === uf && al === ul;
					if (author.email && user?.email) return String(author.email).toLowerCase() === String(user.email).toLowerCase();
					return false;
				};

				const filtered = (list as any[]).filter(isAuthoredByUser);
				setEmployeeNotes(filtered as any[]);
			} catch (err: any) {
				setNotesError(err?.message || 'Failed to fetch employee notes');
			} finally {
				setNotesLoading(false);
			}
		};

		fetchNotes();
	}, [user]);

			// read-only: no create/edit/delete handlers

	if (!user) {
		return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
	}

	return (
		<div className={styles.showUserSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>{user.firstName} {user.lastName}</h2>
				</Grid>
			</Grid>
			<div className={styles.formContainer}>
				{/* Personal Information Section */}
				<div className={styles.section}>
					<h4 className={styles.sectionTitle}>Personal Information</h4>
					
					<div className={styles.fieldGroup}>
						<div className={styles.field}>
							<label className={styles.label}>User Name (Email)</label>
							<span className={styles.value}>{user.email || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>First Name</label>
							<span className={styles.value}>{user.firstName || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Middle Name</label>
							<span className={styles.value}>{user.middleName || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Last Name</label>
							<span className={styles.value}>{user.lastName || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Cell Phone</label>
							<span className={styles.value}>{user.phone || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Gender</label>
							<span className={styles.value}>{user.gender || 'N/A'}</span>
						</div>

					<div className={styles.field}>
						<label className={styles.label}>Marital Status</label>
						<span className={styles.value}>{user.maritalStatus || 'Single'}</span>
					</div>

					<div className={styles.field}>
						<label className={styles.label}>Date of Birth</label>
						<span className={styles.value}>
							{user.dateOfBirth ? dayjs(user.dateOfBirth).format('MM/DD/YYYY') : 'N/A'}
						</span>
					</div>
				</div>
				</div>

				{/* Employment Information Section */}
				<div className={styles.section}>
					<h4 className={styles.sectionTitle}>Employment Information</h4>
					
					<div className={styles.fieldGroup}>
						<div className={styles.field}>
							<label className={styles.label}>Start Date</label>
							<span className={styles.value}>
								{user.startDate ? dayjs(user.startDate).format('MM/DD/YYYY') : 'N/A'}
							</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Hierarchy</label>
							<span className={styles.value}>{user?.hierarchy?.name || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Security Role</label>
							<span className={styles.value}>{user.securityRole?.name || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Active Status</label>
							<Chip 
								label={user.userType?.active ? 'Active' : 'Inactive'} 
								color={user.userType?.active ? 'success' : 'default'}
								size="small"
							/>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>User Must Change Password on Next Login</label>
							<Chip 
								label={user.userMustChangePasswordOnNextLogin ? 'Yes' : 'No'} 
								color={user.userMustChangePasswordOnNextLogin ? 'warning' : 'default'}
								size="small"
							/>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Separation</label>
							<Chip
								label={user.separation ? 'Yes' : 'No'}
								color={user.separation ? 'error' : 'default'}
								size="small"
							/>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Separation Date</label>
							<span className={styles.value}>{user.separationDate ? dayjs(user.separationDate).format('MM/DD/YYYY') : 'N/A'}</span>
						</div>
					</div>
				</div>

				{/* Address Information Section */}
				<div className={styles.section}>
					<h4 className={styles.sectionTitle}>Address Information</h4>
					
					<div className={styles.fieldGroup}>
						<div className={styles.field}>
							<label className={styles.label}>Address Line 1</label>
							<span className={styles.value}>{user.addressLine1 || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Address Line 2</label>
							<span className={styles.value}>{user.addressLine2 || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>City</label>
							<span className={styles.value}>{user.city || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>State</label>
							<span className={styles.value}>{user.state || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Postal Code</label>
							<span className={styles.value}>{user.postcode || 'N/A'}</span>
						</div>

						<div className={styles.field}>
							<label className={styles.label}>Country</label>
							<span className={styles.value}>{user.country || 'N/A'}</span>
						</div>
					</div>
				</div>

				{/* Employee Notes Section (fetched from API, authored by this user) */}
				<div className={styles.section}>
					<h4 className={styles.sectionTitle}>Employee Notes</h4>
					<div className={styles.fieldGroup}>
						<Accordion expanded={notesExpanded} onChange={() => setNotesExpanded(!notesExpanded)} sx={{ width: '100%' }}>
							<AccordionDetails>
								{notesLoading && <div><CircularProgress size={20} color="inherit" /></div>}
								{notesError && <div style={{ color: 'red' }}>{notesError}</div>}
								{!notesLoading && !notesError && (!employeeNotes || employeeNotes.length === 0) && (
									<div>No employee notes authored by this user.</div>
								)}
								{employeeNotes && employeeNotes.length > 0 && (
									<div>
										{employeeNotes.map((note: any) => {
											const id = note?.['@id'] ? parseInt(String(note['@id']).split('/').pop() || '0') : note.id;
											const content = note.note || note.note_text || '';
											const date = note.date || note.date_of_the_note || '';
											const author = note.author ? `${note.author.firstName || note.author.first_name || ''} ${note.author.lastName || note.author.last_name || ''}`.trim() : '';
											return (
												<Box key={id} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
													<div style={{ fontSize: 14, marginBottom: 6 }}><strong>{author || 'Unknown'}</strong> — {date ? new Date(date).toLocaleString() : ''}</div>
													<div style={{ whiteSpace: 'pre-wrap' }}>{stripHtmlTags(content)}</div>
												</Box>
											);
										})}
									</div>
								)}
							</AccordionDetails>
						</Accordion>
					</div>
				</div>

			
			</div>
		</div>
	);
};

export default ShowUserSetting;
